import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { OverlayTrigger, Popover } from "react-bootstrap";
import "./EquationComponentsPopover.scss";
import { withTranslation } from "react-i18next";
import { BiInfoCircle } from "react-icons/bi";
import EquationService from "../../core/service/EquationService";

const resolveDataPoint = (dataPointByCode, code, displayCode) => {
  if (!dataPointByCode) return undefined;
  return dataPointByCode[displayCode] || dataPointByCode[code] || undefined;
};

/**
 * `expressionWithId` uses a single-letter prefix on each data-point code in the expression:
 * - E and N = emission, D and M = not emission.
 * e.g. MECD007a/NECD007b from API → { ECD007a: false, ECD007b: true }.
 */
const isEmissionPrefix = (c) => c === "E" || c === "N";
const isKnownTypePrefix = (c) => isEmissionPrefix(c) || c === "D" || c === "M";

const splitExpressionWithIdParts = (expressionWithId) => {
  if (!expressionWithId || typeof expressionWithId !== "string") return [];
  return expressionWithId
    .split(/[+\-*/() \t\r\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
};

const buildEmissionMapFromExpressionWithId = (expressionWithId) => {
  const map = new Map();
  for (const part of splitExpressionWithIdParts(expressionWithId)) {
    if (part.length < 2) continue;
    const c0 = part[0].toUpperCase();
    if (!isKnownTypePrefix(c0)) continue;
    const rest = part.slice(1);
    if (rest) map.set(rest, isEmissionPrefix(c0));
  }
  return map;
};

/** When the components API is empty or errors: show codes + E/N/D/M emission from the expression only. */
const listLocalOnlyComponents = (expressionWithId) => {
  const out = [];
  for (const part of splitExpressionWithIdParts(expressionWithId)) {
    if (part.length < 2) continue;
    const c0 = part[0].toUpperCase();
    if (!isKnownTypePrefix(c0)) continue;
    const displayCode = part.slice(1);
    if (!displayCode) continue;
    out.push({
      key: part,
      displayCode,
      isEmission: isEmissionPrefix(c0)
    });
  }
  return out;
};

const resolveEmission = (displayCode, rawKey, emissionByCode, dp) => {
  if (emissionByCode && emissionByCode.size) {
    const tryKeys = new Set();
    if (displayCode) {
      tryKeys.add(displayCode);
      const c0 = displayCode[0]?.toUpperCase();
      if (isKnownTypePrefix(c0)) tryKeys.add(displayCode.slice(1));
    }
    if (rawKey) {
      tryKeys.add(rawKey);
      if (rawKey.length > 1) tryKeys.add(rawKey.substring(1));
    }
    for (const k of tryKeys) {
      if (k && emissionByCode.has(k)) return emissionByCode.get(k);
    }
  }
  if (dp) return Boolean(dp.emission);
  return null;
};

const getEmissionSuffix = (isEmission, t) => {
  if (isEmission == null) return null;
  if (!isEmission) {
    return ` (${t("EquationComponentsPopover.notEmission", { defaultValue: "Emission unchecked" })})`;
  }
  return ` (${t("EquationComponentsPopover.emission", { defaultValue: "Emission checked" })})`;
};

const getEmissionRowClassName = (isEmission) => {
  if (isEmission == null) return "";
  return isEmission ? "text-success" : "text-danger";
};

// API names often include "(Emission)" already; we append the same from our suffix, so strip to avoid duplicate

const stripEmissionFromApiName = (nameStr, t) => {
  if (!nameStr || !nameStr.trim()) return nameStr;
  const emission = t("EquationComponentsPopover.emission", { defaultValue: "Emission checked" });
  const notEmission = t("EquationComponentsPopover.notEmission", { defaultValue: "Emission unchecked" });
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return nameStr
    .replace(new RegExp(`\\s*\\(${esc(emission)}\\)\\s*$`, "i"), "")
    .replace(new RegExp(`\\s*\\(${esc(notEmission)}\\)\\s*$`, "i"), "")
    // backend often returns English parentheticals
    .replace(/\s*\(Emission\)\s*$/i, "")
    .replace(/\s*\(Not emission\)\s*$/i, "")
    .trim();
};

const EquationComponentsPopover = ({
  t,
  standardId,
  equationId,
  headerText,
  popoverKey,
  className = "",
  popoverClassName = "",
  iconSize = 14,
  stopPropagation = false,
  showWhenNoEquationId = true,
  dataPoints = null
}) => {
  const [loading, setLoading] = useState(false);
  const [componentsMap, setComponentsMap] = useState(null);

  const dataPointByCode = useMemo(() => {
    if (!dataPoints || !dataPoints.length) return null;
    return dataPoints.reduce((acc, dp) => {
      if (dp?.code) acc[dp.code] = dp;
      return acc;
    }, {});
  }, [dataPoints]);

  const emissionByCode = useMemo(
    () => buildEmissionMapFromExpressionWithId(equationId),
    [equationId]
  );

  const localOnlyRows = useMemo(
    () => listLocalOnlyComponents(equationId),
    [equationId]
  );

  useEffect(() => {
    setComponentsMap(null);
  }, [equationId, standardId]);

  const canShow = showWhenNoEquationId || !!equationId;
  if (!canShow) return null;

  const loadComponents = (show) => {
    if (!show || componentsMap !== null) return;
    if (!equationId) {
      setComponentsMap({});
      return;
    }
    if (!standardId) {
      setComponentsMap({});
      return;
    }

    setLoading(true);
    EquationService.getEquationComponents(standardId, equationId)
      .then((res) => {
        setComponentsMap(res?.data && typeof res.data === "object" ? res.data : {});
      })
      .catch(() => {
        setComponentsMap({});
      })
      .finally(() => setLoading(false));
  };

  const safeKey = (popoverKey || `${standardId || "na"}-${equationId || "na"}`).replace(/[^\w-]/g, "_");
  const entries = Object.entries(componentsMap || {});

  const hasApiList = !loading && !!equationId && entries.length > 0;
  const useLocalOnlyFallback = !loading && !!equationId && !hasApiList && localOnlyRows.length > 0;
  const showEmpty = !loading && !!equationId && !hasApiList && !useLocalOnlyFallback;

  return (
    <OverlayTrigger
      trigger="click"
      placement="right"
      rootClose
      onToggle={loadComponents}
      overlay={(
        <Popover
          id={`eq-components-popover-${safeKey}`}
          className={popoverClassName?.trim() ? popoverClassName.trim() : undefined}
        >
          <Popover.Header as="h6">{headerText || equationId || t("Equation.label", { defaultValue: "Equation" })}</Popover.Header>
          <Popover.Body>
            {!equationId && <div className="text-muted">{t("NoData.label", { defaultValue: "No data" })}</div>}
            {loading && <div>{t("Loading.label", { defaultValue: "Loading" })}</div>}
            {showEmpty && <div className="text-muted">{t("NoData.label", { defaultValue: "No data" })}</div>}
            {hasApiList && (
              <ul className="mb-0 ps-3">
                {entries.map(([code, name]) => {
                  const displayCode = code.length > 0 ? code.substring(1) : code;
                  const dp = resolveDataPoint(dataPointByCode, code, displayCode);
                  const nameStr = typeof name === "string" ? name : (name == null ? "" : String(name));
                  const isEmission = resolveEmission(displayCode, code, emissionByCode, dp);
                  const emissionDetail = getEmissionSuffix(isEmission, t);
                  const displayName =
                    emissionDetail != null
                      ? stripEmissionFromApiName(nameStr, t)
                      : nameStr;
                  const rowClass = getEmissionRowClassName(isEmission);
                  return (
                    <li key={code} className={rowClass || undefined}>
                      <strong>{displayCode}</strong>
                      {displayName ? `: ${displayName}` : ""}
                      {emissionDetail}
                    </li>
                  );
                })}
              </ul>
            )}
            {useLocalOnlyFallback && (
              <ul className="mb-0 ps-3">
                {localOnlyRows.map((row) => {
                  const emissionDetail = getEmissionSuffix(row.isEmission, t);
                  const rowClass = getEmissionRowClassName(row.isEmission);
                  return (
                    <li key={row.key} className={rowClass || undefined}>
                      <strong>{row.displayCode}</strong>
                      {emissionDetail}
                    </li>
                  );
                })}
              </ul>
            )}
          </Popover.Body>
        </Popover>
      )}
    >
      <span
        className={["equation-components-popover-trigger d-inline-block", className].filter(Boolean).join(" ")}
        role="img"
        aria-label={t("ESGAnalytics.equationComponents.label", { defaultValue: "Equation components" })}
        onClick={(e) => {
          if (stopPropagation) e.stopPropagation();
        }}
      >
        <BiInfoCircle size={iconSize} />
      </span>
    </OverlayTrigger>
  );
};

EquationComponentsPopover.propTypes = {
  t: PropTypes.func.isRequired,
  standardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  equationId: PropTypes.string,
  headerText: PropTypes.string,
  popoverKey: PropTypes.string,
  className: PropTypes.string,
  popoverClassName: PropTypes.string,
  iconSize: PropTypes.number,
  stopPropagation: PropTypes.bool,
  showWhenNoEquationId: PropTypes.bool,
  dataPoints: PropTypes.array
};

export default withTranslation()(EquationComponentsPopover);
